const getMonth = (offset = 0) => {
  const today = new Date();
  const currentDay = today.getDate();

  // Move to the 1st of the month to avoid overflow issues
  const newDate = new Date(today.getFullYear(), today.getMonth() + offset, 1);

  // Get the last valid day of the target month
  const lastDay = new Date(
    newDate.getFullYear(),
    newDate.getMonth() + 1,
    0
  ).getDate();

  // Restore the original day, clamped to the last day of the month
  newDate.setDate(Math.min(currentDay, lastDay));

  return new Intl.DateTimeFormat("en-GB", { month: "long" }).format(newDate);
};

function formatDate(inputDateString) {
  if (!inputDateString) return "";
  // Parse the input date string into a Date object
  const inputDate = new Date(inputDateString);
  const day = String(inputDate.getDate()).padStart(2, "0");
  const month = String(inputDate.getMonth() + 1).padStart(2, "0"); // Month is 0-based
  const year = inputDate.getFullYear().toString().substring(2, 4);

  return `${day}.${month}.${year}`;
}

function formatDateShortMonth(inputDateString) {
  if (!inputDateString) return "";
  const inputDate = new Date(inputDateString);
  return inputDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
}

function applyCountryCode(phone) {
  if (phone && phone[0] === "0") {
    let countryPhone = phone.substring(1);
    countryPhone = `+44${countryPhone}`;
    return countryPhone;
  }

  return phone;
}

const yesterday = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday;
};

function formatDateTime(dateString) {
  if (!dateString) {
    return "";
  }

  const date = new Date(dateString);
  const today = new Date();

  if (date.toDateString() === today.toDateString()) {
    return `Today, ${date.toLocaleString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  } else if (date.toDateString() === yesterday().toDateString()) {
    return `Yesterday, ${date.toLocaleString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  } else {
    return `${date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  }
}

function formatCountdown(inputDateString) {
  if (!inputDateString) return null;

  // Parse the input date string into a Date object
  const inputDate = new Date(inputDateString);
  var today = new Date();

  // Check if inputDate is valid
  if (isNaN(inputDate)) return null;

  // Set the time of 'today' to 00:00:00 for accurate day comparison
  today.setHours(0, 0, 0, 0);

  // Calculate the difference in days
  const oneDay = 24 * 60 * 60 * 1000; // milliseconds in a day
  const diffDays = Math.floor((inputDate - today) / oneDay);

  if (diffDays > 1) {
    // More than one day difference
    return diffDays + " days";
  } else if (diffDays === 1) {
    // Input date is tomorrow
    return "tomorrow";
  } else if (diffDays === 0 && inputDate > new Date()) {
    // Same day, but future time
    return (
      "today at " +
      inputDate.getHours() +
      ":" +
      (inputDate.getMinutes() < 10 ? "0" : "") +
      inputDate.getMinutes()
    );
  }

  return null;
}

function formatTimeAgo(inputDateString) {
  if (!inputDateString) return null;

  // Parse the input date string into a Date object
  const inputDate = new Date(inputDateString);
  const today = new Date();

  // Check if inputDate is valid
  if (isNaN(inputDate)) return null;

  // Set the time of 'today' to 00:00:00 for accurate day comparison
  today.setHours(0, 0, 0, 0);

  // Set inputDate time to 00:00:00 as well for day-based comparison
  inputDate.setHours(0, 0, 0, 0);

  // Calculate the difference in days
  const oneDay = 24 * 60 * 60 * 1000; // milliseconds in a day
  const diffDays = Math.floor((today - inputDate) / oneDay);

  if (diffDays === 0) {
    // Input date is today
    return "today";
  } else if (diffDays === 1) {
    // Input date was yesterday
    return "yesterday";
  } else if (diffDays < 30) {
    // Input date was within the last month, return in days
    return diffDays + " days ago";
  } else {
    // Calculate the difference in months
    const diffMonths = Math.floor(diffDays / 30);

    if (diffMonths === 1) {
      // Input date was last month
      return "last month";
    } else {
      // Input date was more than one month ago
      return diffMonths + " months ago";
    }
  }
}

function formatCommMethod(input, number) {
  if (!input) {
    return null;
  }

  switch (input.toLowerCase()) {
    case "sms":
      return `SMS ${number}`;
    case "qrcode":
      return `QR code`;
    case "link":
      return `URL`;
    default:
      return null;
  }
}

function formatAmount(inputAmountString, trimSymbol = false) {
  if (!inputAmountString && inputAmountString !== 0) {
    return null;
  }

  // Parse the input amount string to a floating-point number
  const inputAmount = parseFloat(inputAmountString);

  // Format the amount with the currency symbol (£) and desired formatting
  const formattedAmount = inputAmount.toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Display or use the formatted amount
  return trimSymbol ? formattedAmount.substring(1) : formattedAmount; // Output: "£246,120.32"
}

function formatKebabCase(kebabStr) {
  if (!kebabStr) return "";

  return kebabStr
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function formatPascalCase(pString) {
  if (!pString) return "";

  return (
    pString
      // Split the string at each point a lowercase to uppercase transition occurs
      .split(/(?=[A-Z])/)
      // Convert the first character of each word to uppercase, and the rest to lowercase
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      // Join the words with a space
      .join(" ")
  );
}

function formatSnakeCase(snakeCaseStr) {
  if (!snakeCaseStr) return "";

  return snakeCaseStr
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function formatSortCode(sortCode) {
  if (!sortCode) return "";

  return (
    sortCode.substring(0, 2) +
    "-" +
    sortCode.substring(2, 4) +
    "-" +
    sortCode.substring(4, 6)
  );
}

function formatAddress(address) {
  if (!address) return "";

  return address.line1 + ", " + address.postalCode + ", " + address.city;
}

function formatYesNo(condition) {
  return condition ? "Yes" : "No";
}

const restrictTextLength = (input, length) => {
  if (!input) {
    return input;
  }

  if (input.length > length) {
    return input.substring(0, length) + "...";
  }
  return input;
};

const addressFormatter = (address) => {
  if (!address) return "";
  return address.line1 + ", " + address.county + ", " + address.postcode;
};

const replaceTokens = (tokenValues, template) => {
  // tokenValues = [{ token: "customer", value: "xyz" }, { token: "amount", value: "£1234.45" }]

  let output = template; // Get the value of the specified input

  tokenValues.forEach(({ token, value }) => {
    const regex = new RegExp(`{${token}}`, "g");
    output = output.replace(regex, value);
  });

  return output;
};

export {
  applyCountryCode,
  formatDate,
  formatDateTime,
  formatAmount,
  formatKebabCase,
  formatSnakeCase,
  formatSortCode,
  formatAddress,
  formatCountdown,
  formatCommMethod,
  formatPascalCase,
  formatYesNo,
  restrictTextLength,
  formatTimeAgo,
  replaceTokens,
  getMonth,
  yesterday,
  formatDateShortMonth,
  addressFormatter,
};
